<template>
  <div class="inspi-wrapper">
    <div
      v-if="popupState !== false"
      @click="popupState = false"
      class="inspi-backdrop"
    ></div>
    <div
      class="inspi-popup product-info"
      v-show="popupState === 'product-info'"
    >
      <div class="popup-inner">
        <div class="popup-grid">
          <div class="popup-image-slider" ref="swipeable">
            <img
              v-if="selectedProductImages.length == 1"
              :src="selectedProductImages[0]"
            />
            <InspireSlider
              v-else
              :images="selectedProductImages"
            ></InspireSlider>
          </div>
          <div class="inspi-popup-content">
            <div class="popup-close clickable" @click="popupState = false">
              <i class="c-icon icon-times" />
            </div>
            <h3 class="inspi-popup-content-heading">
              {{ selectedProduct.name }}
            </h3>
            <div v-if="globalProps.showPrice" class="inspi-popup-pricing">
              <span class="inspi-popup-pricing-label">{{
                selectedProduct.price_label
              }}</span>
              <span class="inspi-popup-pricing-from">{{
                globalProps.textFromPrice +
                ' ' +
                formatPrice(selectedProduct.price_from)
              }}</span>
            </div>
            <p
              v-if="selectedProduct.description"
              class="inspi-popup-description inspi-popup-base-text"
            >
              {{ selectedProduct.description }}
            </p>
            <hr class="divider" />

            <BaseButton
              v-if="isInCart(selectedProduct.id)"
              class="button-primary"
              @click.native="handleRemoveFromCart(selectedProduct.id, false)"
              :properties="{
                fullwidth: true,
                text: globalProps.textRemoveFromCart,
              }"
            />
            <BaseButton
              v-else
              class="button-primary"
              @click.native="handleAddToCart(selectedProduct.id)"
              :properties="{
                fullwidth: true,
                text: globalProps.textAddToCart,
              }"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="inspi-popup added-to-cart"
      v-show="popupState === 'added-to-cart'"
    >
      <div class="popup-inner">
        <div class="inspi-popup-content">
          <div class="popup-close clickable" @click="popupState = false">
            <i class="c-icon icon-times" />
          </div>
          <h3>
            {{
              selectedProduct.name + ' ' + globalProps.textHasBeenAddedToCart
            }}
          </h3>

          <BaseButton
            class="button-outline"
            @click.native="popupState = false"
            :properties="{
              fullwidth: true,
              text: globalProps.textAddMoreProducts,
            }"
          />

          <BaseButton
            class="button-primary"
            @click.native="popupState = 'cart'"
            :properties="{fullwidth: true, text: globalProps.textGoToWishlist}"
          />
        </div>
      </div>
    </div>

    <div class="inspi-popup cart" v-show="popupState === 'cart'">
      <div class="inspi-popup-inner">
        <div class="inspi-popup-header">
          <div>{{ globalProps.textWishlistTitle }}</div>
          <span class="popup-close clickable" @click="popupState = false">
            <i class="c-icon icon-times" />
          </span>
        </div>
        <div class="inspi-popup-content">
          <div class="inspi-popup-content-left">
            <h3 class="inspi-popup-heading">
              {{ globalProps.textProductsTitle }}
              <span class="inspi-popup-heading-clear" @click="handleClearCart">
                {{ globalProps.textWishlistClearAll }}
              </span>
            </h3>
            <div class="inspi-popup-products">
              <div
                v-for="(product, index) in selectedProducts"
                class="inspi-popup-product"
                :key="index"
              >
                <img class="inspi-popup-product-image" :src="product.img_url" />
                <div class="inspi-popup-product-content">
                  <h3 class="inspi-popup-product-title">{{ product.name }}</h3>
                  <div
                    v-if="globalProps.showPrice"
                    class="inspi-popup-product-pricing"
                  >
                    <span>{{ selectedProduct.price_label }}</span>
                    <span>{{ formatPrice(selectedProduct.price_from) }}</span>
                  </div>
                  <span
                    class="popup-close clickable"
                    @click="handleRemoveFromCart(product.id)"
                  >
                    <i class="c-icon icon-times" />
                  </span>
                </div>
              </div>
            </div>
            <div class="inspi-popup-questions">
              <template v-if="options && options.length > 0">
                <h3 class="inspi-popup-heading">
                  {{ globalProps.textServicesTitle }}
                </h3>
                <div class="inspi-popup-services">
                  <div
                    v-for="option in options"
                    class="inspi-grid-item"
                    :key="option.id"
                    @click="toggleSelectedOption(option)"
                  >
                    <div class="inspi-option">
                      <div
                        class="inspi-option-icon"
                        :class="{
                          selected: selectedOptionIds.includes(
                            option.id || option.name,
                          ),
                        }"
                      >
                        <template
                          v-if="
                            selectedOptionIds.includes(option.id || option.name)
                          "
                        >
                          <i class="c-icon icon-check" />
                        </template>
                        <template v-else>
                          <i class="c-icon icon-plus" />
                        </template>
                      </div>
                      <h3 class="inspi-option-title">{{ option.title }}</h3>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="inspi-popup-content-right">
            <form
              @submit.prevent="handleSubmit"
              class="inspi-popup-form"
              ref="fields"
              novalidate
            >
              <div class="field" :class="{error: formErrors.name}">
                <label>{{ globalProps.textFormFirstLastname }}</label>
                <input
                  v-model="form.name"
                  type="text"
                  :placeholder="globalProps.textFormFirstLastname"
                  @input="clearFieldError('name')"
                />
                <div class="error-message">{{ formErrors.name }}</div>
              </div>
              <div class="field" :class="{error: formErrors.company}">
                <label>{{ globalProps.textFormCompany }}</label>
                <input
                  v-model="form.company"
                  type="text"
                  :placeholder="globalProps.textFormCompany"
                  @input="clearFieldError('company')"
                />
                <div class="error-message">{{ formErrors.company }}</div>
              </div>
              <div class="field" :class="{error: formErrors.email}">
                <label>{{ globalProps.textFormEmail }}</label>
                <input
                  v-model="form.email"
                  type="text"
                  :placeholder="globalProps.textFormEmail"
                  @input="clearFieldError('email')"
                />
                <div class="error-message">{{ formErrors.email }}</div>
              </div>
              <div class="field" :class="{error: formErrors.phone}">
                <label>{{ globalProps.textFormPhone }}</label>
                <input
                  v-model="form.phone"
                  type="text"
                  :placeholder="globalProps.textFormPhone"
                  @input="clearFieldError('phone')"
                />
                <div class="error-message">{{ formErrors.phone }}</div>
              </div>
              <div class="field" :class="{error: formErrors.comments_2}">
                <label>{{ globalProps.textFormComments }}</label>
                <textarea
                  v-model="form.comments_2"
                  @input="clearFieldError('comments_2')"
                ></textarea>
                <div class="error-message">{{ formErrors.comments_2 }}</div>
              </div>

              <BaseButton
                class="button-primary"
                :loading="submitting"
                :properties="{
                  fullwidth: true,
                  text: globalProps.textFormSubmit,
                }"
              />
            </form>
            <div class="inspi-popup-content-right-background"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="inspi-popup thank-you" v-show="popupState === 'thank-you'">
      <div class="popup-inner">
        <div class="inspi-popup-content">
          <!-- <div class="popup-close clickable" @click="popupState = false">
            <i class="c-icon icon-times" />
          </div> -->
          <h3 class="inspi-popup-content-heading">
            {{ globalProps.textThankyouTitle }}
          </h3>
          <p>
            {{ globalProps.textThankyouText }}
          </p>

          <Button @click="popupState = false" class="button-primary">{{
            globalProps.textThankyouCloseWindow
          }}</Button>
        </div>
      </div>
    </div>

    <div
      class="inspi-sticky"
      :class="{hidden: !inCartProductIds.length}"
      @click="popupState = 'cart'"
    >
      <span>{{ globalProps.textStickyButton }}</span>
      <span class="inspi-sticky-icon">{{ selectedProducts.length }}</span>
    </div>
  </div>
</template>

<script>
import BaseButton from '../BaseButton/BaseButton';
import localState from './mixin';
import InspireSlider from './InspireSlider.vue';
import currency from 'currency.js';

export default {
  mixins: [localState],

  props: {},

  components: {
    BaseButton,
    InspireSlider,
  },

  inject: ['funnelInfo'],

  data() {
    return {
      submitting: false,
      formErrors: {},
      formHasErrors: false,
      form: {
        name: '',
        company: '',
        email: '',
        phone: '',
        comments: '',
        comments_2: '',
      },
    };
  },
  watch: {
    inCartProductIds() {
      this.updateAllProducts();
    },
    popupState(val) {
      if (val) {
        document.body.classList.add('prevent-scroll');

        // When cart is opened try to focus first field
        // if (val == 'cart') {
        //   this.focusFirstField();
        // }
      } else {
        document.body.classList.remove('prevent-scroll');
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
    document.addEventListener(
      'openPrintWishlist',
      this.openPrintWishlistListner,
    );
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener(
      'openPrintWishlist',
      this.openPrintWishlistListner,
    );
  },
  methods: {
    openPrintWishlistListner() {
      this.popupState = 'cart';
    },
    // Close on Escape
    handleKeydown(e) {
      if (e.key == 'Escape') {
        this.popupState = false;
      }
    },
    formatPrice(priceNumber) {
      const price = currency(priceNumber, {
        symbol: '€',
        separator: '.',
        decimal: ',',
        formatWithSymbol: true,
      });

      let formatedPrice = price.format();

      // if cents are 0, remove them and add ,- (half kastlijntje)
      if (price.cents() === 0) {
        formatedPrice = formatedPrice.replace(',00', ',-');
      }

      return formatedPrice;
    },
    validateForm() {
      const requiredFields = ['name', 'email'];

      // Reset errors
      this.formErrors = {};

      // Check if required fields are set
      requiredFields.forEach((field) => {
        if (!this.form[field]) {
          this.formErrors[field] = this.globalProps.textFormErrorRequired;
        }
      });

      // Test e-mail
      if (!testEmail(this.form['email'])) {
        this.formErrors['email'] = this.globalProps.textFormErrorEmail;
      }

      // Set formHasErrors
      this.formHasErrors = Object.values(this.formErrors).filter(
        (v) => v,
      ).length;

      // If form has errors, focus error field
      if (this.formHasErrors) {
        this.focusFirstField('error');
      }

      return this.formHasErrors;
    },
    clearFieldError(name) {
      this.formErrors[name] = '';
    },
    // Focus the first field, or when validating, the first field that has an error
    focusFirstField(firstOrError = 'first') {
      this.$nextTick(() => {
        if (!this.$refs.fields) return;
        const selector =
          firstOrError == 'first'
            ? 'input[type=email], input[type=text], input[type=number], textarea'
            : '.error input, .error textarea';

        const input = this.$refs.fields.querySelector(selector);

        if (input) {
          input.focus();
        }
      });
    },
    handleSubmit() {
      // Check for errors
      if (this.validateForm()) {
        return;
      }

      const data = {
        fields: [
          ...Object.keys(this.form).map((key) => ({
            name: key,
            value: this.form[key],
            id: key,
          })),
          {
            name: 'Products',
            id: 'products',
            value: this.selectedProducts,
          },
          {
            name: 'Geselecteerde Opties',
            id: 'options',
            value: this.selectedOptions
              .map((option) => option.title)
              .join(', '),
          },
        ],
        project_id: this.funnelInfo.projectId,
        funnel_id: this.funnelInfo.funnelId,
        page_id: this.funnelInfo.pageId,
        form_id: 'inspi-form',
        automations: [],
      };

      this.handleClearCart();

      if (!window.__CONVERDY__.is_export) {
        this.submitting = true;

        fetch(`${window.__CONVERDY__.api_url}/public/form`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
          .then((response) => response.json())
          .then((data) => {
            console.log('Success:', data);
            this.submitting = false;
            this.popupState = 'thank-you';
          })
          .catch((error) => {
            console.error('Error:', error);
            this.submitting = false;
          });
      }
    },
  },
  computed: {
    selectedProductImages() {
      if (!this.selectedProduct) return [];
      return [
        this.selectedProduct.img_featured || this.selectedProduct.img_url,
        ...(this.selectedProduct.img_urls || []).map((item) => item.src),
      ];
    },
  },
};

function testEmail(email) {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}
</script>

<style scoped lang="scss">
.clickable {
  cursor: pointer;
}

// SHARED
.inspi-wrapper {
  color: var(--cinspiration-text-color);
  .popup-inner {
    pointer-events: all;
  }
}

.inspi-backdrop {
  position: fixed;
  width: 100vw;
  height: 100vh;
  background: hsla(0, 100%, 0%, 0.8);
  z-index: 9999;
  top: 0;
  left: 0;
}
.inspi-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  pointer-events: none;

  p {
    margin-bottom: 15px;
  }
  hr {
    margin-bottom: 15px;
  }
  .button-primary,
  .button-outline {
    padding: 8px;
    border: 1px solid var(--primary-grid-color);
    border-radius: 20px;
  }
  .button-primary {
    background: var(--primary-grid-color);
    color: white;
  }

  .button-outline {
    border-radius: 20px;
    color: var(--primary-grid-color);
  }
  .popup-close {
    padding: 10px;
    width: 30px;
    height: 30px;
    background: #f5f5f5;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }

  textarea,
  input[type='text'] {
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 10px;
    overflow: auto;
    outline: none;
    box-shadow: none;
    resize: none;
    padding: 10px;
    font-size: 16px;
  }
  textarea {
    min-height: 100px;
  }

  label {
    display: block;
    margin-bottom: 5px;
    font-size: 14px;
  }

  .inspi-popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid #ccc;
  }
}
.inspi-popup-heading {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  color: var(--cinspiration-title-color);
  margin-bottom: 10px;
  font-weight: 600;
}
.inspi-popup-heading-clear {
  font-size: 14px;
  color: var(--color-text);
  font-weight: 600;
  cursor: pointer;
}
.inspi-popup-content-heading {
  font-size: 24px;
  margin-bottom: 14px;
  font-weight: 600;
  color: var(--cinspiration-title-color);
}

.inspi-popup-pricing {
  font-size: 12px;
  margin-bottom: 10px;
  font-weight: 500;
}

.inspi-popup-pricing .inspi-popup-pricing-from {
  margin-left: 15px;
}

// PRODUCT INFO
.product-info {
  .popup-inner {
    max-height: 100vh;
    width: 800px;
    background: #fff;
    border-radius: 25px;
    overflow: scroll;

    .popup-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 30px;

      @media screen and (max-width: 769px) {
        display: flex;
        flex-direction: column;
      }

      .popup-image-slider {
        display: flex;
        min-height: 500px;
        background: #ccc;
        @media screen and (max-width: 769px) {
          min-height: 300px;
          height: 300px;
        }

        img {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }
      }
      .inspi-popup-content {
        padding: 25px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .popup-close {
        align-self: flex-end;
      }
    }
  }
}
.added-to-cart {
  .popup-inner {
    max-width: 350px;
    max-height: 100vh;
    width: 800px;
    background: #fff;
    border-radius: 25px;
    .inspi-popup-content {
      padding: 25px;
      display: flex;
      flex-direction: column;
    }

    .button-outline {
      margin-bottom: 15px;
    }

    h3 {
      font-weight: 600;
      margin-bottom: 15px;
    }
    .popup-close {
      align-self: flex-end;
    }
  }
}

.thank-you {
  .popup-inner {
    max-height: 100vh;
    width: 800px;
    background: #fff;
    border-radius: 25px;
    .inspi-popup-content {
      padding: 25px;
      display: flex;
      flex-direction: column;
    }

    .popup-close {
      align-self: flex-end;
    }
  }
}

// CART
.inspi-popup.cart {
  .button-primary {
    margin-bottom: 15px;
  }
  .inspi-popup-services {
    margin-bottom: 15px;
  }

  .inspi-popup-form .field {
    margin-bottom: 10px;
  }

  .inspi-option {
    display: flex;
    align-items: center;
    border: 1px solid #dcdcdc;
    border-radius: 5px;
    padding: 5px;
    color: #333;
    font-size: 14px;
    margin-bottom: 5px;
  }
  .inspi-option .inspi-option-icon {
    padding: 5px;
    color: #fff;
    background: #ccc;
    width: 25px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    margin-right: 5px;
    flex-shrink: 0;
  }
  .inspi-option .inspi-option-icon.selected {
    padding: 5px;
    color: #fff;
    background: var(--primary-grid-color);
  }

  .inspi-popup-inner {
    max-height: 100vh;
    width: 800px;
    background: #fff;
    border-radius: 25px;
    pointer-events: all;
    overflow: hidden;
  }

  .inspi-popup-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .inspi-popup-content-left {
    padding: 15px 45px;
  }
  .inspi-popup-content-right {
    padding: 15px 45px;
    border-left: 1px solid #dcdcdc;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .inspi-popup-form {
    z-index: 2;
  }
  .inspi-popup-content-right-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.09;
    background-color: var(--primary-grid-color);
  }

  .inspi-popup-products {
    border: 1px solid #dcdcdc;
    border-radius: 10px;
    max-height: 405px;
    overflow: scroll;
  }
  .inspi-popup-product {
    display: grid;
    grid-template-columns: 60px 1fr;
    padding: 10px;
    border-bottom: 1px solid #dcdcdc;
  }
  .inspi-popup-product:last-child {
    border-bottom: 0px;
  }
  .inspi-popup-product-image {
    object-fit: cover;
    border-radius: 10px;
    width: 60px;
    height: 60px;
  }

  .inspi-popup-product-content {
    display: flex;
    font-size: 14px;
  }

  .inspi-popup-product-title {
    margin-left: 10px;
  }

  .inspi-popup-product-pricing {
    display: flex;
    margin-left: 5px;
  }
  .inspi-popup-questions {
    margin-top: 20px;
  }
  .inspi-popup-questions textarea {
    width: 100%;
    height: 100px;
    background: #fff;
    border: 1px solid #dcdcdc;
    border-radius: 10px;
    overflow: auto;
    outline: none;
    box-shadow: none;
    resize: none;
    padding: 10px;
  }
  .inspi-popup-questions span {
    display: block;
    color: #ccc;
    margin-bottom: 10px;
  }

  .inspi-popup-specificaties {
    margin-bottom: 20px;
  }
}

@media screen and (max-width: 769px) {
  .inspi-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 30px;
  }
  .inspi-popup {
    position: fixed;
    width: 100vw;
    height: 100vh;
    background: #fff;
    border-radius: 0;
    overflow: hidden;
  }
  .inspi-popup .inspi-popup-content {
    display: grid;
    grid-template-columns: 1fr;
    overflow: scroll;
    height: 100%;
  }
  .inspi-popup.cart .inspi-popup-content {
    display: grid;
    grid-template-columns: 1fr;
    overflow: scroll;
    height: calc(100vh - 60px);
  }
  .inspi-popup.cart .inspi-popup-content-right {
    border-left: 0;
  }
}

// THANK YOU

.thank-you {
  .popup-inner {
    max-width: 300px;
    max-height: 100vh;
    width: 800px;
    background: #fff;
    border-radius: 25px;
    .inspi-popup-content {
      padding: 25px;
      display: flex;
      flex-direction: column;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
      grid-row-gap: 0px;
    }

    .inspi-popup-header {
      margin-bottom: 10px;
    }

    .left,
    .right {
      display: flex;
      flex-direction: column;
      input {
        margin-bottom: 10px;
      }
    }

    .left {
      grid-area: 1 / 1 / 2 / 2;
      padding-right: 10px;
      border-right: 1px solid #dcdcdc;
    }
    .right {
      grid-area: 1 / 2 / 2 / 3;
      padding-left: 10px;
    }

    .bottom {
      grid-area: 2 / 1 / 3 / 3;
    }
  }
}

// STICKY
.inspi-sticky {
  position: fixed;
  padding: 10px 15px 14px 15px;
  font-weight: 500;
  bottom: 0;
  background: #333;
  color: #fff;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
  left: 50%;
  transform: translate(-50%, 4px);
  z-index: 100;
  opacity: 1;
  transition: 200ms ease-in-out;

  &:hover {
    transform: translate(-50%, 0px);
  }
}

.inspi-sticky.hidden {
  opacity: 0;
  transform: translate(-50%, 100%);
}

.inspi-sticky .inspi-sticky-icon {
  padding: 5px;
  color: #fff;
  background: var(--primary-grid-color);
  margin-left: 20px;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 25px;
}

/* form field */

input {
  font-size: 16px;
}
.error {
  .error-message {
    font-size: 12px;
    display: block;
    color: var(--color-error) !important;
  }
  input {
    border-color: var(--color-error) !important;
    border-width: 1px;
  }
}
</style>
